import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Package, FileText, Calendar, Clock, Plus, RefreshCw } from 'lucide-react';

const MaterialHistory = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('vet_portal_token');
      const rawResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/material-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (rawResponse.status === 401) {
        localStorage.removeItem('vet_portal_token');
        localStorage.removeItem('vet_portal_clinic');
        localStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return;
      }
      const response = rawResponse.clone();

      if (!response.ok) {
        throw new Error('Failed to fetch material requests');
      }

      const data = await response.json();
      setRequests(data.requests || []);
    } catch (error) {
      toast.error('Failed to load material requests', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const getStatusBadge = (status) => {
    const variants = {
      'pending': 'default',
      'processing': 'secondary',
      'shipped': 'default',
      'delivered': 'default',
      'completed': 'default',
      'fulfilled': 'default',
      'cancelled': 'destructive'
    };

    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      'processing': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      'shipped': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      'delivered': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      'completed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      'fulfilled': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    };

    return (
      <Badge 
        variant={variants[status] || 'default'} 
        className={colors[status]}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="material-history-page">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-primary">Material Request History</h1>
            <p className="text-muted-foreground mt-2">
              Track your marketing material orders
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchRequests}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => navigate('/material-request')}
              data-testid="new-request-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Request
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : requests.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Package className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Requests Yet</h3>
              <p className="text-muted-foreground text-center mb-6">
                You haven't submitted any material requests yet.
              </p>
              <Button onClick={() => navigate('/material-request')}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Request
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.request_id} data-testid={`request-${request.request_id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">
                        Request #{request.request_id?.substring(0, 8) || 'N/A'}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Submitted: {formatDate(request.created_at)}</span>
                      </div>
                    </div>
                    {getStatusBadge(request.status || 'pending')}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Materials Requested */}
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">Materials Requested:</h4>
                      <div className="space-y-1">
                        {request.quantity_tags > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <Package className="h-4 w-4 text-primary" />
                            <span>{request.quantity_tags} Pet Tags</span>
                          </div>
                        )}
                        {request.quantity_brochures > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <FileText className="h-4 w-4 text-primary" />
                            <span>{request.quantity_brochures} Brochures</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Delivery Info */}
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">Delivery Information:</h4>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        {request.expected_delivery && (
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>Expected: {formatDate(request.expected_delivery)}</span>
                          </div>
                        )}
                        {request.tracking_number && (
                          <div>
                            <span className="font-medium">Tracking: </span>
                            <span className="font-mono">{request.tracking_number}</span>
                          </div>
                        )}
                        {request.delivery_notes && (
                          <div className="mt-2">
                            <span className="font-medium">Notes: </span>
                            <p className="text-sm">{request.delivery_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status Timeline */}
                  {request.status_history && request.status_history.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-sm mb-2">Status Timeline:</h4>
                      <div className="space-y-1">
                        {request.status_history.map((history, idx) => (
                          <div key={`${history.timestamp}-${history.status}`} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="font-medium">{formatDate(history.timestamp)}:</span>
                            <span>{history.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MaterialHistory;
